import { openProjectContent } from "../../../api/content/workflowsApi";
import type { ThreadOverview } from "../../../api/threads/threadDecoders";
import { forkThread, postMessage, prepareStart, requestTurn, startThread, type ForkChoice } from "../../../api/threads/threadsApi";
import { ORIGIN_INPUT, PASSAGES_INPUT, originInputText, passagesInputText } from "../../memories/keep/passageInput";
import { EDITED_ITEM_INPUT } from "../entries/humanStepResponse";
import type { EntryPassageRef } from "./threadIntent";
import { notifyThreadListChanged } from "../data/threadListEvents";
import { entryBindings } from "./run/entrySelection";
import { keepPreparedAnswer } from "./preparedAnswer";
import type { ThreadSubject } from "./threadIntent";
import { startRoutesOf, type StartRouteDraft } from "../llmChoice/startRouteDraft";

/**
 * The preparation's Start, and the only code that creates Threads (WA-11, the `thread-start`
 * check). Each command is idempotent by the preparation's request key: Start again after a
 * failure, Back or reload returns the Thread the first request made (T-10, FR-THREAD-1).
 */

export interface RunStart {
  readonly projectId: string;
  readonly title: string;
  /** A Workflow `VersionRef`, or `null` for a free Thread. */
  readonly workflow: string | null;
  /** The content version the Thread binds (T-3); `null` opens the project's first one. */
  readonly contentVersion: string | null;
  readonly corpusVersion: string | null;
  readonly entries: readonly ThreadSubject[];
  /** Values for the Workflow's inputs, by exposed name or destination (ADR-0032 D7, built provisionally). */
  readonly inputValues?: Readonly<Record<string, unknown>>;
  /** The LLMs the start chose (F106, TR-A2-04): sent so the first agent step runs on them. */
  readonly routes?: StartRouteDraft | null;
  readonly requestKey: string;
}

export async function startRun(start: RunStart): Promise<ThreadOverview> {
  const thread = await startThread({
    projectId: start.projectId,
    title: start.title,
    workflow: start.workflow,
    bindings: {
      content_version: start.contentVersion ?? (await openProjectContent(start.projectId)),
      corpus_version: start.corpusVersion,
      ...entryBindings(start.entries),
    },
    clientRequestId: start.requestKey,
    inputValues: start.inputValues,
    routes: start.routes === undefined || start.routes === null ? null : startRoutesOf(start.routes),
  });
  notifyThreadListChanged();
  return thread;
}

export interface AuthoringStart {
  readonly projectId: string;
  readonly title: string;
  readonly workflow: string | null;
  readonly contentVersion: string;
  /** The prepared first message: sent in a free Thread, the first person step's answer in a Workflow. */
  readonly message: string;
  readonly requestKey: string;
}

/** A Thread about an item (F37, AU-2): free, or on a Workflow whose first step asks a person. */
export async function startAuthoring(start: AuthoringStart): Promise<ThreadOverview> {
  const thread = await startThread({
    projectId: start.projectId,
    title: start.title,
    workflow: start.workflow,
    bindings: { content_version: start.contentVersion, corpus_version: null, subject: null },
    clientRequestId: start.requestKey,
  });
  if (start.workflow === null) {
    await postMessage(thread.threadId, { text: start.message, mentions: [], clientMessageId: `${start.requestKey}-message` });
  } else {
    keepPreparedAnswer(thread.threadId, start.message);
  }
  notifyThreadListChanged();
  return thread;
}

export interface EditStart {
  readonly projectId: string;
  readonly title: string;
  /** The library's "Edit an item" Workflow, as the project's listing marks it. */
  readonly workflow: string;
  /** The model version listed, which the Thread binds: the same for every try of the edit. */
  readonly contentVersion: string;
  /** What the step edits, as its `item` input names it (`editedItem.ts`). */
  readonly item: string;
  /** The edit's key: one Thread and one step for every try of the same edit. */
  readonly requestKey: string;
}

export interface StartedEdit {
  readonly threadId: string;
  /** The request of the edit step, which the save answers. */
  readonly entry: number;
}

/**
 * "Edit" by hand (ADR-0031 D2): the first save of an edit starts a Thread of the library's
 * "Edit an item", which every project pins (ADR-0037 D6), with the item as its first step's
 * input. Given its input at the start, that person step is asked at once (F33): the edit
 * answers that request. Only a step the start did not ask is asked here. The writes carry the
 * edit's key, so a save tried again with the same start returns the same Thread and request.
 * A project held for the process ontology upgrade starts only the edit Thread of an item its
 * hold names (V21).
 */
export async function startEdit(start: EditStart): Promise<StartedEdit> {
  const bindings = { content_version: start.contentVersion, corpus_version: null, subject: null };
  // The first step's key as the server flattens it, so a sub-workflow's step is named in full.
  const prepared = await prepareStart({ projectId: start.projectId, workflow: start.workflow, bindings, inputValues: {} });
  const editStep = prepared.steps[0]?.stepKey ?? null;
  const thread = await startThread({
    projectId: start.projectId,
    title: start.title,
    workflow: start.workflow,
    bindings,
    clientRequestId: start.requestKey,
    inputValues: editStep === null ? undefined : { [`${editStep}.${EDITED_ITEM_INPUT}`]: start.item },
  });
  notifyThreadListChanged();
  const waiting = thread.projection.open_human_step ?? null;
  if (waiting !== null) return { threadId: thread.threadId, entry: waiting.request_entry };
  const firstStep = editStep ?? thread.detail.steps?.[0]?.step_key;
  if (firstStep === undefined) throw new Error("The \"Edit an item\" Workflow has no step to edit in.");
  const request = await requestTurn(thread.threadId, { kind: "step", stepKey: firstStep, clientRequestId: `${start.requestKey}-edit`, inputValues: { [EDITED_ITEM_INPUT]: start.item } });
  return { threadId: thread.threadId, entry: request.number };
}

export interface KeepMemoryStart {
  readonly projectId: string;
  readonly title: string;
  /** The library's "Keep a memory" Workflow, as the project's listing marks it. */
  readonly workflow: string;
  readonly contentVersion: string;
  /** The Thread the passages come from: the memory's origin. */
  readonly originThreadId: string;
  readonly passages: readonly EntryPassageRef[];
  readonly requestKey: string;
}

/**
 * Keep passages as a memory (ADR-0031 D5): the memory's own small Thread, a run of "Keep a
 * memory", and its first step asked with the passages and the origin, so the step's view is
 * the keep form. Idempotent by the request key, as every start.
 */
export async function startKeepMemory(start: KeepMemoryStart): Promise<ThreadOverview> {
  const thread = await startThread({
    projectId: start.projectId,
    title: start.title,
    workflow: start.workflow,
    bindings: { content_version: start.contentVersion, corpus_version: null, subject: null },
    clientRequestId: start.requestKey,
  });
  const firstStep = thread.detail.steps?.[0]?.step_key;
  if (firstStep !== undefined) {
    await requestTurn(thread.threadId, {
      kind: "step",
      stepKey: firstStep,
      clientRequestId: `${start.requestKey}-keep`,
      inputValues: { [PASSAGES_INPUT]: passagesInputText(start.passages), [ORIGIN_INPUT]: originInputText(start.originThreadId) },
    });
  }
  notifyThreadListChanged();
  return thread;
}

export interface ForkStart extends ForkChoice {
  readonly threadId: string;
  readonly title: string;
  readonly requestKey: string;
}

/** A fork (T-10): nothing runs at a fork; the new Thread is saved until its host attaches a connector. */
export async function startFork(start: ForkStart): Promise<ThreadOverview> {
  const { threadId, requestKey, ...request } = start;
  const thread = await forkThread(threadId, { ...request, clientRequestId: requestKey });
  notifyThreadListChanged();
  return thread;
}
